import { Injectable } from '@angular/core';
import { BaseProvider } from './base';
import { API } from './api';

export interface Organization {
    name: string;
    organizationIdentifier: string;
    ownerIdentifier: string;
}

export interface OrganizationUser {
    active: boolean;
    email: string;
    name: string;
    role: string;
    userIdentifier: string;
    accepted?: boolean;
}

export interface OrganizationUsersResponse {
    organization: Organization;
    users: OrganizationUser[];
}

@Injectable()
export class OrganizationProvider extends BaseProvider {
    private orgIdentifier: string;
    private usersList: OrganizationUsersResponse | null;

    constructor(private api: API) {
        super();
    }

    /**
     * Create a new organization
     * @param name name of the new organization
     */
    public async createOrganization(name: string): Promise<any> {
        try {
            const payload: any = {
                name
            };
            const response: any = await this.api.post('organization', payload);
            return response;
        } catch (err) {
            throw err;
        }
    }

    /**
     * ger current user's organization
     */
    public async getOrganization(): Promise<any> {
        try {
            const response: any = await this.api.get('organization');
            if (response.success && response.message) {
                return response.message;
            } else {
                return null;
            }
        } catch (err) {
            throw err;
        }
    }

    /**
     * Rename an organizaiton
     * @param orgIdentifier organization identifier
     * @param newName new name for organization
     */
    public async renameOrganization(orgIdentifier: string, newName: string): Promise<any> {
        try {
            const payload: any = { newName };
            const response: any = await this.api.post(`organization/${orgIdentifier}/rename`, payload);
            return response;
        } catch (err) {
            throw err;
        }
    }

    /**
     * Get all users in an organization
     * @param orgIdentifier organization identifier
     */
    public async getOrganizationUsers(orgIdentifier: string): Promise<OrganizationUsersResponse> {
        try {
            const response: any = await this.api.get(`organization/${orgIdentifier}/users`);

            return response.success ? response.message : [];
        } catch (err) {
            throw err;
        }
    }

    /**
     * change user's role in organziaiton
     * @param newRole new role for the user
     * @param userIdentifer user identifier
     * @param orgIdentifier organization identifer
     */
    public async changeUserRole(newRole: string, userIdentifer: string, orgIdentifier: string): Promise<boolean> {
        try {
            const payload: any = { role: newRole };
            const response: any = await this.api.post(
                `organization/${orgIdentifier}/user/${userIdentifer}/role`,
                payload
            );
            return response.success;
        } catch (err) {
            throw err;
        }
    }

    /**
     * delete a user from organizaiton
     * @param userIdentifier user identifier
     * @param orgIdentifier organizaiton identifier
     */
    public async deleteUser(userIdentifier: string, orgIdentifier: string): Promise<boolean> {
        try {
            const response: any = await this.api.delete(`organization/${orgIdentifier}/user/${userIdentifier}`);
            return response.success;
        } catch (err) {
            throw err;
        }
    }

    public async deleteInvitation(token: string, orgIdentifier: string): Promise<boolean> {
        try {
            const response: any = await this.api.delete(`organization/${orgIdentifier}/invitations/${token}`);
            return response.success;
        } catch (err) {
            throw err;
        }
    }

    public async inviteUser(invitations: any[], orgIdentifier: string, isResend: boolean = false): Promise<boolean> {
        try {
            const payload = {
                invites: invitations
            };
            const response = await this.api.post(
                `organization/${orgIdentifier}/invitations${isResend ? '?resend=true' : ''}`,
                payload
            );
            return response.success;
        } catch (error) {
            throw error;
        }
    }

    public async getInvitedUsers(orgIdentifier: string): Promise<any[]> {
        try {
            const response = await this.api.get(`organization/${orgIdentifier}/invitations`);

            return response.success ? response.message : [];
        } catch (error) {
            throw error;
        }
    }

    /**
     * get current user's organziaiton
     */
    public async getUsers(): Promise<any> {
        try {
            let users;
            const resp: any = await this.getOrganization();
            if (resp) {
                this.orgIdentifier = resp.organizationIdentifier;
                const response = await this.getOrganizationUsers(this.orgIdentifier);
                users = response.users;
            }
            return users ? users : '';
        } catch (err) {
            throw err;
        }
    }

    /**
     * Check if organization has tager users
     */
    public async hasTaggerUsers(orgIdentifier: string): Promise<boolean> {
        try {
            if (!this.usersList) {
                this.usersList = await this.getOrganizationUsers(orgIdentifier);
            }

            return this.usersList.users.some(userItem => userItem.role === 'User');
        } catch (err) {
            throw err;
        }
    }

    /**
     * Reset cached data
     */
    public async resetInfo() {
        this.usersList = null;
    }
}



// WEBPACK FOOTER //
// ./src/providers/organization.ts